import { createContext, useContext, useState, type PropsWithChildren } from 'react'

import type { TMessageKey } from '@/src/lang/useTranslate'

import type { TOtpAuthenticationMode } from '../../services/useOtpAuthentication'

type TOtpAuthenticationSession = {
  email: string
  name: string
  mode?: TOtpAuthenticationMode
  csrfToken: string
  nameErrorKey?: TMessageKey
}

type TOtpAuthenticationContext = TOtpAuthenticationSession & {
  updateSession: (session: Partial<TOtpAuthenticationSession>) => void
}

const OtpAuthenticationContext = createContext<TOtpAuthenticationContext | null>(null)

const initialSession: TOtpAuthenticationSession = {
  email: '',
  name: '',
  // We don't want the csrfToken to be exposed in the url.
  csrfToken: '',
}

export const OtpAuthenticationProvider = ({ children }: PropsWithChildren) => {
  const [session, setSession] = useState(initialSession)

  const updateSession = (nextSession: Partial<TOtpAuthenticationSession>) => {
    setSession((currentSession) => ({ ...currentSession, ...nextSession }))
  }

  return (
    <OtpAuthenticationContext.Provider value={{ ...session, updateSession }}>
      {children}
    </OtpAuthenticationContext.Provider>
  )
}

export const useOtpAuthenticationSession = () => {
  const context = useContext(OtpAuthenticationContext)
  if (!context) throw new Error('useOtpAuthenticationSession must be used within OtpAuthenticationProvider')
  return context
}
